import { useEffect, useRef, useState } from "react";
import { Play, VolumeX } from "lucide-react";
import { useSite } from "@/lib/useSite";
import Chapter from "@/components/Chapter";

const thumb = (id, quality = "hqdefault") => `https://i.ytimg.com/vi/${id}/${quality}.jpg`;

function Poster({ id, alt, className = "" }) {
  // maxresdefault doesn't exist for every video; fall back to hqdefault.
  return (
    <img
      src={thumb(id, "maxresdefault")}
      onError={(e) => {
        if (!e.currentTarget.dataset.fallback) {
          e.currentTarget.dataset.fallback = "1";
          e.currentTarget.src = thumb(id);
        }
      }}
      alt={alt}
      className={className}
    />
  );
}

export default function WatchSection() {
  const { data, isLoading } = useSite();
  const videos = (data?.watch_videos || []).filter((v) => v.video_id);
  const [activeIdx, setActiveIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [startMuted, setStartMuted] = useState(true);
  const [soundOn, setSoundOn] = useState(false);
  const playerBoxRef = useRef(null);
  const iframeRef = useRef(null);
  const hasVideos = videos.length > 0;

  // Autoplay (muted, as browsers require) once the player scrolls into view.
  useEffect(() => {
    const el = playerBoxRef.current;
    if (!hasVideos || !el || playing) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    if (!("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStartMuted(true);
          setSoundOn(false);
          setPlaying(true);
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasVideos, playing]);

  // Hide the whole section (no empty gap) until the admin adds a video.
  if (isLoading || videos.length === 0) return null;

  const active = videos[Math.min(activeIdx, videos.length - 1)];
  const title = active.title || "Watch this video";

  // A click is a user gesture, so these start with sound.
  const select = (i) => {
    setActiveIdx(i);
    setStartMuted(false);
    setSoundOn(true);
    setPlaying(true);
  };

  const playWithSound = () => {
    setStartMuted(false);
    setSoundOn(true);
    setPlaying(true);
  };

  const unmute = () => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: "command", func: "unMute", args: [] }),
      "https://www.youtube-nocookie.com"
    );
    setSoundOn(true);
  };

  return (
    <section id="watch" data-testid="watch-section" className="mx-auto max-w-[1400px] px-5 pb-24 sm:px-8 sm:pb-32">
      <Chapter
        no="02"
        kicker="Watch"
        heading="See it in action."
        sub="Product walkthroughs and installations, straight from the yard and the job site."
      />

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <div className="min-w-0">
          <div
            ref={playerBoxRef}
            data-testid="watch-player"
            className="relative aspect-video w-full overflow-hidden rounded-3xl bg-ink shadow-2xl shadow-ink/20"
          >
            {playing ? (
              <iframe
                key={active.video_id}
                ref={iframeRef}
                src={`https://www.youtube-nocookie.com/embed/${active.video_id}?autoplay=1&mute=${startMuted ? 1 : 0}&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
                title={title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 h-full w-full border-0"
              />
            ) : (
              <button
                type="button"
                data-testid="watch-play-button"
                onClick={playWithSound}
                aria-label={`Play video: ${title}`}
                className="group absolute inset-0 h-full w-full"
              >
                <Poster id={active.video_id} alt="" className="h-full w-full object-cover" />
                <span className="absolute inset-0 bg-ink/25 transition-colors group-hover:bg-ink/10" />
                <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink shadow-xl transition-all duration-300 group-hover:scale-110 group-hover:bg-srs group-hover:text-white sm:h-24 sm:w-24">
                  <Play size={30} className="ml-1" fill="currentColor" />
                </span>
              </button>
            )}
            {playing && startMuted && !soundOn && (
              <button
                type="button"
                data-testid="watch-unmute-button"
                onClick={unmute}
                className="absolute right-4 top-4 z-10 flex items-center gap-2 rounded-full bg-white px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink shadow-lg transition-colors hover:bg-srs hover:text-white"
              >
                <VolumeX size={14} /> Tap for sound
              </button>
            )}
          </div>
          {active.title && (
            <p data-testid="watch-title" className="mt-5 font-display text-xl font-semibold tracking-tight text-ink sm:text-2xl">
              {active.title}
            </p>
          )}
        </div>

        {videos.length > 1 && (
          <div className="min-w-0">
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-subtle">
              {videos.length} videos
            </p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-1">
              {videos.map((v, i) => (
                <button
                  key={v.id}
                  type="button"
                  data-testid={`watch-thumb-${i}`}
                  onClick={() => select(i)}
                  aria-label={`Play video: ${v.title || `Video ${i + 1}`}`}
                  className={`group text-left transition-all duration-300 ${
                    v.id === active.id ? "" : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <span
                    className={`relative block aspect-video w-full overflow-hidden rounded-2xl ${
                      v.id === active.id ? "ring-2 ring-srs ring-offset-2" : ""
                    }`}
                  >
                    <img src={thumb(v.video_id, "mqdefault")} alt="" loading="lazy" className="h-full w-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center bg-ink/20 opacity-0 transition-opacity group-hover:opacity-100">
                      <Play size={22} className="text-white" fill="currentColor" />
                    </span>
                  </span>
                  <span className="mt-2 block truncate font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
                    {v.title || `Video ${String(i + 1).padStart(2, "0")}`}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
